/**
 * Ce qui circule dans une session live.
 *
 * # Un geste, pas un message MIDI
 *
 * On aurait pu faire transiter les octets MIDI bruts : c'est ce que le clavier
 * envoie, et le studio sait déjà les interpréter. On ne le fait pas, et c'est
 * volontaire.
 *
 * MIDI est un protocole de *câble* : il porte des canaux, des messages de
 * synchronisation, des variantes constructeur, et trois façons d'exprimer un
 * relâchement. Rien de tout ça ne veut dire quoi que ce soit entre deux
 * personnes qui jouent ensemble. Ce module décrit donc ce qui se passe — une
 * touche s'enfonce, une touche se relâche, la pédale bouge — et laisse aux
 * bords le soin de traduire.
 *
 * Le bénéfice est immédiat : la source peut être un clavier MIDI, le clavier de
 * l'ordinateur, le micro, ou un morceau rejoué ; la destination peut être un
 * piano, un affichage, un enregistreur. Aucun des deux n'a à connaître l'autre.
 */

/** Un geste de jeu, indépendant de sa source et de son transport. */
export type LiveEvent =
  | { kind: "noteOn"; midi: number; velocity: number }
  | { kind: "noteOff"; midi: number }
  | { kind: "sustain"; down: boolean };

/**
 * Un geste, et les deux façons de le situer dans le temps.
 *
 * `at` est l'instant du geste sur l'horloge **du service**, en millisecondes
 * réduites à 32 bits — la même horloge chez tout le monde, celle contre
 * laquelle chaque poste s'aligne. C'est ce qui permet au récepteur de mesurer
 * le trajet en lisant la sienne à l'arrivée (voir `lib/live/transit.ts`), et
 * ce qui sert au regroupement en trames. Zéro veut dire « inconnu » : un
 * émetteur pas encore aligné.
 *
 * `tick` est la position sur le transport **partagé** : c'est par là que
 * passe l'alignement musical — où la note se pose sur la grille, et quand elle
 * sonne chez le partenaire.
 */
export interface TimedLiveEvent {
  event: LiveEvent;
  at: number;
  tick: number;
}

/** Vrai si `value` est un entier MIDI valide. */
export function isMidiNumber(value: number): boolean {
  return Number.isInteger(value) && value >= 0 && value <= 127;
}
